import { SubscriptionPlanConfig } from '@waitroom/models';

type SPC = SubscriptionPlanConfig;
export type FeatureTrans = [string, Dict<string>?];

const isFeatureEnabled = <TKey extends keyof SPC>(key: TKey, config: SPC): boolean => {
  const v1 = config?.[key];
  return v1 && typeof v1 === 'object' && 'enabled' in v1 ? !!v1.enabled : false;
};

const isFeatureVisible = <TKey extends keyof SPC>(
  key: TKey,
  config1: SPC,
  config2?: SPC,
): boolean => {
  const f1Enabled = isFeatureEnabled(key, config1);
  if (!config2) return f1Enabled;
  return f1Enabled && !isFeatureEnabled(key, config2);
};

const transMapFn: Dict<(curr: SPC, compare?: SPC) => FeatureTrans | undefined> = {
  integrations: (curr: SPC, compare?: SPC) => {
    const ci = curr?.integrations;
    if (
      ci?.enabled &&
      (!compare || !compare.integrations?.enabled || compare.integrations.apps !== ci?.apps)
    ) {
      const apps = ci.apps?.join(', ') || '';
      return [apps.length ? 'integrations' : 'integrationsAll', { apps }];
    }
    return;
  },
  stream: (curr: SPC, compare?: SPC) => {
    const ci = curr?.stream;
    if (
      ci?.enabled &&
      (!compare || !compare.stream?.enabled || compare.stream.quality !== ci?.quality)
    ) {
      return [ci.quality === 1080 ? 'stream1080' : 'stream720'];
    }
    return;
  },
  aiFeedLocal: (curr: SPC, compare?: SPC) => {
    const ci = curr?.aiFeed;
    if (
      ci?.enabled &&
      (!compare || !compare.aiFeed?.enabled || compare.aiFeed.local !== ci?.local)
    ) {
      return ['aiFeedLocal'];
    }
    return;
  },
  // TEMP: fix for showing these options on Custom plan as they are not part of the plan configs
  team: (_: SPC, __?: SPC) => {
    return ['team'];
  },
  meetingSharing: (_: SPC, __?: SPC) => {
    return ['meetingSharing'];
  },
};

export const getFeatureTrans = (
  key: string,
  config: SPC,
  compare?: SPC,
): FeatureTrans | undefined => {
  const fn = transMapFn[key];
  if (fn) return fn(config, compare);
  return isFeatureVisible(key as keyof SPC, config, compare) ? [key] : undefined;
};
